import { Injectable, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { APP_CONFIG } from '@application/config';

import {
  IconSearchResult,
  IconSearchResponse,
  UnifiedIcon,
  IconSearchParams,
  IconCacheEntry,
  IconServiceConfig,
  IconSearchState,
  IconCategory
} from '../entities/icon.interface';
import { LanguageStore } from '@settings/language/data-access';

import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class IconsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl.search_service;
  private readonly languageStore = inject(LanguageStore);
  
  public readonly currentLanguage = computed(() => this.languageStore.getCurrentLanguage()?.split('-')?.[0]);

  private readonly cache = new Map<string, IconCacheEntry>();
  
  private readonly config: IconServiceConfig = {
    cacheTTL: 5 * 60 * 1000, 
    defaultLanguage: 'pt',
    defaultPerPage: 100,
    debounceTime: 300
  };


  public getIconCategories(): Observable<IconCategory[]> {
    const url = `${this.apiUrl}/api/icons/categories`;
    const httpParams = new HttpParams().set('language', this.currentLanguage() || this.config.defaultLanguage);

    return this.http.get<string[]>(url, { 
      params: httpParams,
      withCredentials: true 
    }).pipe(
      map(categories => categories.map(category => ({
        name: category,
        displayName: this.formatCategoryDisplayName(category)
      }))),
      catchError(error => {
        console.error('Erro ao buscar categorias de ícones:', error);
        return throwError(() => new Error('Falha ao carregar categorias. Verifique sua conexão e tente novamente.'));
      })
    );
  }

  private formatCategoryDisplayName(category: string): string {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }


  public searchIcons(params: IconSearchParams): Observable<UnifiedIcon[]> {
    const cacheKey = this.getCacheKey(params);
    const cached = this.getFromCache(cacheKey);
    
    if (cached) {
      return of(cached.data);
    }

    return this.searchExternalIcons(params).pipe(
      map(response => {
        const unified = this.mapExternalToUnified(response.results);
        this.setCache(cacheKey, unified, response.found);
        return unified;
      }),
      catchError(error => {
        console.error('Erro ao buscar ícones externos:', error);
        return throwError(() => new Error('COMPONENTS.ICON_GALERY.MODAL_SEARCH_FAILED'));
      })
    );
  }

  private searchExternalIcons(params: IconSearchParams): Observable<IconSearchResponse> {
    const url = `${this.apiUrl}/api/icons/search`;
    let httpParams = new HttpParams()
      .set('q', params.query)
      .set('language', params.language)
      .set('perPage', params.perPage.toString())
      .set('page', params.page.toString());

    if (params.category) {
      httpParams = httpParams.set('category', params.category);
    }

    return this.http.get<IconSearchResponse>(url, { 
      params: httpParams,
      withCredentials: true 
    });
  }


  private mapExternalToUnified(external: IconSearchResult[]): UnifiedIcon[] {
    return external.map(icon => ({
      id: icon.iconKey.replace('fi-rr-', ''),
      text: icon.name,
      icon: icon.iconKey.replace('fi-rr-', ''),
      description: icon.description,
      category: icon.category,
      tags: icon.tags,
      score: icon.score
    })).sort((a, b) => 
      (b.score || '0').localeCompare(a.score || '0')
    );
  }

  private getCacheKey(params: IconSearchParams): string {
    return `${params.query}_${params.language}_${params.perPage}_${params.page}_${params.category || 'all'}`;
  }


  private getFromCache(key: string): IconCacheEntry | null {
    const cached = this.cache.get(key);
    if (!cached) {
      return null;
    }

    const now = DateTime.now().toMillis();
    if (now - cached.timestamp > this.config.cacheTTL) {
      this.cache.delete(key);
      return null;
    }

    return cached;
  }

  private setCache(key: string, data: UnifiedIcon[], totalFound: number): void {
    const entry: IconCacheEntry = {
      data,
      timestamp: DateTime.now().toMillis(),
      query: key,
      totalFound
    };
    this.cache.set(key, entry);
  }

  public clearExpiredCache(): void {
    const now = DateTime.now().toMillis();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.config.cacheTTL) {
        this.cache.delete(key);
      }
    }
  }

  public clearCache(): void {
    this.cache.clear();
  }

  public getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }


  public searchWithDebounce(searchTerm$: Observable<string>): Observable<UnifiedIcon[]> {
    return searchTerm$.pipe(
      debounceTime(this.config.debounceTime),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query.trim()) {
          return of([]);
        }

        const params: IconSearchParams = {
          query,
          language: this.currentLanguage() || this.config.defaultLanguage,
          perPage: this.config.defaultPerPage,
          page: 1
        };

        return this.searchIcons(params);
      })
    );
  }


  public retrySearch(params: IconSearchParams): Observable<UnifiedIcon[]> {
    const cacheKey = this.getCacheKey(params);
    this.cache.delete(cacheKey);
    
    return this.searchIcons(params);
  }
}